
import React, { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PuttingResult } from "@/types/puttingResults";
import { SwingTendency } from "@/components/putting/training/charts/swing/types";
import {
  groupResultsByExercise,
  prepareExerciseData,
  calculateTendencyData,
  hasTendencies,
  shouldShowSwingExercise
} from "./utils/exerciseUtils";
import ExerciseStatistics from "./components/ExerciseStatistics";
import ExerciseProgressionChart from "./components/ExerciseProgressionChart";
import ExerciseTendencyAnalysis from "./components/ExerciseTendencyAnalysis";

interface ExerciseProgressionTabsProps {
  trainingResults: PuttingResult[];
  selectedLevel: string | null;
  swingTendencies?: SwingTendency[];
  traineeUserId?: string;
}

const ExerciseProgressionTabs: React.FC<ExerciseProgressionTabsProps> = ({
  trainingResults,
  selectedLevel,
  swingTendencies = [],
  traineeUserId
}) => {
  const { t } = useTranslation('training');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    console.log('ExerciseProgressionTabs: Received swing tendencies:', swingTendencies.length);
    console.log('ExerciseProgressionTabs: Selected level:', selectedLevel);
    console.log('ExerciseProgressionTabs: Swing tendencies data:', swingTendencies);
  }, [swingTendencies, selectedLevel]);

  const exerciseGroups = groupResultsByExercise(trainingResults);
  
  // Ajouter les types d'exercices de swing pour tous les niveaux
  const swingExerciseTypes = ['Fers', 'BoisDeParcours', 'Driver'];
  const allExerciseTypes = new Set([
    ...Object.keys(exerciseGroups).filter(type => exerciseGroups[type].length > 0),
    ...swingExerciseTypes.filter(type => shouldShowSwingExercise(type, swingTendencies, selectedLevel))
  ]);
  
  const exerciseTypes = Array.from(allExerciseTypes);

  console.log('ExerciseProgressionTabs: All exercise types:', exerciseTypes);
  console.log('ExerciseProgressionTabs: Exercise groups keys:', Object.keys(exerciseGroups));
  console.log('ExerciseProgressionTabs: Swing tendencies available:', swingTendencies.length);

  if (exerciseTypes.length === 0) {
    return (
      <Card>
        <CardContent className="p-6 text-center text-gray-500">
          <p>Aucun résultat d'exercice trouvé pour les critères sélectionnés.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('results.exerciseProgressionTitle')}</CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue={exerciseTypes[0]} className="w-full">
          <TabsList className="grid w-full grid-cols-7 mb-4">
            {exerciseTypes.slice(0, 7).map((exerciseType) => {
              const translatedName = t(`results.exerciseTypes.${exerciseType}`, { defaultValue: exerciseType });
              console.log(`Tab translation: key=results.exerciseTypes.${exerciseType}, result=${translatedName}`);
              return (
                <TabsTrigger key={exerciseType} value={exerciseType} className="text-sm">
                  {translatedName}
                </TabsTrigger>
              );
            })}
          </TabsList>
          
          {exerciseTypes.map((exerciseType) => {
            const exerciseData = prepareExerciseData(exerciseGroups[exerciseType] || [], selectedLevel);
            const tendencyData = calculateTendencyData(exerciseType, swingTendencies, selectedLevel);
            
            // Grouper par test_id (ou testName comme fallback) pour avoir plusieurs lignes
            // Utiliser test_id garantit des couleurs cohérentes quelle que soit la langue
            const testGroups: Record<string, any[]> = {};
            const testIdToName: Record<string, string> = {}; // Map testId to testName for display
            exerciseData.forEach(data => {
              // Use testId for grouping to ensure consistent colors across languages
              // Fall back to testName if testId is not available
              const key = data.testId && data.testId !== 'Standard' ? data.testId : data.testName;
              if (!testGroups[key]) {
                testGroups[key] = [];
                testIdToName[key] = data.testName; // Store the display name for legend
              }
              testGroups[key].push(data);
            });

            return (
              <TabsContent key={exerciseType} value={exerciseType}>
                <div className="space-y-6">
                  {/* Graphique de progression - seulement si on a des données d'exercice */}
                  {Object.keys(testGroups).length > 0 && (
                    <ExerciseProgressionChart testGroups={testGroups} testIdToName={testIdToName} />
                  )}

                  {/* Tendances directionnelles si disponibles pour tous les niveaux */}
                  {hasTendencies(exerciseType) && !loading && (
                    <ExerciseTendencyAnalysis
                      exerciseType={exerciseType}
                      tendencyData={tendencyData}
                      selectedLevel={selectedLevel}
                      swingTendencies={swingTendencies}
                      traineeUserId={traineeUserId}
                    />
                  )}

                  {/* Statistiques de l'exercice - seulement si on a des résultats */}
                  {(exerciseGroups[exerciseType] && exerciseGroups[exerciseType].length > 0) && (
                    <ExerciseStatistics
                      exerciseType={exerciseType}
                      results={exerciseGroups[exerciseType]}
                    />
                  )}
                  
                  {/* Message si pas de données d'entraînement mais des tendances disponibles */}
                  {(!exerciseGroups[exerciseType] || exerciseGroups[exerciseType].length === 0) && 
                   hasTendencies(exerciseType) && (
                    <div className="p-4 bg-blue-50 rounded-lg text-center">
                      <p className="text-blue-700 font-medium">
                        Aucun résultat d'entraînement pour {exerciseType}
                      </p>
                      <p className="text-blue-600 text-sm mt-1">
                        {tendencyData.total > 0 
                          ? "Seules les données de tendances directionnelles sont disponibles."
                          : `Aucune donnée disponible pour le niveau ${selectedLevel || 'sélectionné'}.`
                        }
                      </p>
                    </div>
                  )}
                </div>
              </TabsContent>
            );
          })}
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default ExerciseProgressionTabs;
